import React from 'react';
import {
  WorkspaceFilterBarSkeleton,
  WorkspaceTableSkeleton,
  WorkspaceTabsSkeleton,
  type WorkspaceTableSkeletonColumn,
} from '@/components/reusable/WorkspaceSkeleton';

/**
 * The single loading state for Commerce (`/home/commerce`).
 *
 * Opening it used to walk through a bare route spinner (the `<Suspense>`
 * fallback), the `FeatureRoute` "Loading..." spinner, a blank `RoleRoute`
 * frame, a centred "Loading commerce…" line and — while workspaces were still
 * loading — the "Connect a business account" screen, before the workspace
 * painted its own tabs and table skeleton. All of those now render this, which
 * draws the workspace's tab strip, the products toolbar and the products
 * table, so the layout appears once and only the rows change when data lands.
 */

// Products table in list view: select, product, source, price, stock, status,
// sync, row actions.
const PRODUCTS_TABLE_COLUMNS: WorkspaceTableSkeletonColumn[] = [
  { width: 'w-4' },
  { width: 'w-[70%]' },
  { width: 'w-16', cellClassName: 'hidden sm:table-cell' },
  { width: 'w-14' },
  { width: 'w-10', cellClassName: 'hidden md:table-cell' },
  { width: 'w-16' },
  { width: 'w-20', cellClassName: 'hidden lg:table-cell' },
  { width: 'w-8', actions: true },
];

/** Tabs: Products, Orders, Carts, Offers. */
export const CommerceTabsSkeleton: React.FC = () => <WorkspaceTabsSkeleton widths={[64, 52, 40, 44]} />;

/** Products toolbar: search, filter chips, then Import, Add product and the overflow menu. */
export const CommerceToolbarSkeleton: React.FC = () => (
  <WorkspaceFilterBarSkeleton filters={3} actions={[84, 120, 36]} />
);

/** The products table on its own, inside the tab's padded card shell. */
export const CommerceTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 8 }) => (
  <div className="px-[15px] pb-8">
    <WorkspaceTableSkeleton
      columns={PRODUCTS_TABLE_COLUMNS}
      rows={rows}
      data-testid="commerce-table-skeleton"
    />
  </div>
);

export const CommercePageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-w-0 flex-col overflow-hidden bg-white"
    aria-busy="true"
    aria-label="Loading commerce"
    data-testid="commerce-page-skeleton"
  >
    <CommerceTabsSkeleton />
    <div className="min-h-0 flex-1 overflow-hidden">
      <CommerceToolbarSkeleton />
      <CommerceTableSkeleton />
    </div>
  </div>
);

export default CommercePageSkeleton;
